import { getLoadDetail } from '../../../services/loads/loadsService';
import {
    applyLoadDetailToListCaches,
    isPendingCompletedLoad,
} from './loadListCacheDelta';
import { applyListRowDeltaToQueryClient } from '../../../utils/entityListRowCache';

/** Wait for a LOAD_DETAILS audit burst (tare + trigger to stage 4) before fetching. */
export const LOAD_DETAIL_REFRESH_DEBOUNCE_MS = 400;

/**
 * Coalesce rapid SignalR load events for one id into a single getLoadDetail.
 * Ignore stale responses when a newer burst has already been scheduled.
 */
export function createLoadDetailRefreshScheduler({
    fetchLoadDetail = getLoadDetail,
    applyToCaches = applyLoadDetailToListCaches,
    debounceMs = LOAD_DETAIL_REFRESH_DEBOUNCE_MS,
} = {}) {
    const timers = new Map();
    const gens = new Map();

    const clearTimer = (id) => {
        const timer = timers.get(id);
        if (timer) clearTimeout(timer);
        timers.delete(id);
    };

    const bumpGen = (id) => {
        const gen = (gens.get(id) ?? 0) + 1;
        gens.set(id, gen);
        return gen;
    };

    return {
        schedule(qc, id, crudOp, isCancelled) {
            const loadId = Number(id);
            if (!qc || !Number.isFinite(loadId)) return;

            if (crudOp === 'D') {
                clearTimer(loadId);
                bumpGen(loadId);
                applyToCaches(qc, loadId, 'D', null);
                return;
            }

            const gen = bumpGen(loadId);
            clearTimer(loadId);
            timers.set(loadId, setTimeout(() => {
                timers.delete(loadId);
                fetchLoadDetail(loadId, null, { cacheBust: true })
                    .then((data) => {
                        if (gens.get(loadId) !== gen) return;
                        if (isCancelled?.()) return;
                        const wrapped = {
                            ...data,
                            __fromSignalR: true,
                            __signalRRefreshAt: Date.now(),
                        };
                        qc.setQueryData(['loadDetail', loadId], wrapped);
                        qc.setQueryData(['loadDetail', String(loadId)], wrapped);
                        applyToCaches(qc, loadId, crudOp, data);
                    })
                    .catch(() => {
                        if (gens.get(loadId) !== gen) return;
                        if (isCancelled?.()) return;
                        if (isPendingCompletedLoad(qc, loadId)) return;
                        applyListRowDeltaToQueryClient(qc, 'incompleteLoads', { deleteIds: [loadId] });
                        applyListRowDeltaToQueryClient(qc, 'completedLoads', { deleteIds: [loadId] });
                    });
            }, debounceMs));
        },
        dispose() {
            for (const timer of timers.values()) clearTimeout(timer);
            timers.clear();
        },
    };
}
